import React, { useState, useEffect, useRef } from 'react';
import { Bell, MessageSquare, AlertTriangle, AlertCircle, Info } from '../icons';
import Tooltip from './Tooltip';

export interface Notification {
  id: string;
  userId: number;
  module: 'zamowienia' | 'sprzedaz' | 'kontroling' | 'global';
  type: 'chat' | 'system' | 'warning' | 'info';
  title: string;
  message: string;
  read: boolean;
  active?: boolean; // Whether notification is still active
  createdAt: string;
  data?: any;
  link?: string;
}

interface NotificationBellProps {
  notifications: Notification[];
  onMarkAsRead: (id: string) => void;
  onMarkAllAsRead: () => void;
  onNotificationClick: (notification: Notification) => void;
}

/**
 * NotificationBell component - part of unified TopBar
 */
export const NotificationBell: React.FC<NotificationBellProps> = ({
  notifications,
  onMarkAsRead,
  onMarkAllAsRead,
  onNotificationClick
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Count only active, unread notifications
  // Badge color: red for warning, yellow for info
  const activeUnreadNotifications = notifications.filter(n => !n.read && (n.active !== false));
  const unreadCount = activeUnreadNotifications.length;
  
  // Count by type for badge colors
  const warningCount = activeUnreadNotifications.filter(n => n.type === 'warning').length;
  const infoCount = activeUnreadNotifications.filter(n => n.type === 'info').length;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'chat':
        return <MessageSquare className="w-4 h-4" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4" />;
      case 'system':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <Info className="w-4 h-4" />;
    }
  };

  const getNotificationColor = (type: Notification['type']) => {
    switch (type) {
      case 'chat': return 'text-blue-500';
      case 'warning': return 'text-yellow-500';
      case 'system': return 'text-purple-500';
      default: return 'text-gray-500';
    }
  };

  const formatTime = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      
      if (diffMins < 1) return 'teraz';
      if (diffMins < 60) return `${diffMins} min temu`;
      
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours}h temu`;
      
      const diffDays = Math.floor(diffHours / 24);
      if (diffDays < 7) return `${diffDays}d temu`;
      
      return date.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit' });
    } catch {
      return '';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Tooltip content="Powiadomienia" position="bottom">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="relative flex items-center justify-center text-text text-slate-700 hover:text-accent hover:text-blue-600 transition-colors p-1 cursor-pointer"
          aria-label="Powiadomienia"
        >
          <Bell className="w-5 h-5" />
          
          {unreadCount > 0 && (
            <span 
              style={{ 
                position: 'absolute',
                top: -2,
                right: -2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 16,
                height: 16,
                padding: unreadCount > 9 ? '0 4px' : 0,
                fontSize: '10px',
                fontWeight: 700,
                lineHeight: 1,
                color: '#ffffff',
                backgroundColor: '#ef4444',
                borderRadius: '9999px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
              }}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </Tooltip>

      {isOpen && (
        <div style={{ 
          position: 'absolute', 
          right: 0, 
          marginTop: '8px', 
          width: '384px', 
          backgroundColor: '#ffffff', 
          border: '1px solid #e2e8f0', 
          borderRadius: '8px', 
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)', 
          zIndex: 50, 
          maxHeight: '600px', 
          display: 'flex', 
          flexDirection: 'column' 
        }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', margin: 0 }}>
              Powiadomienia {unreadCount > 0 && `(${unreadCount})`}
            </h3>
            {unreadCount > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); onMarkAllAsRead(); }}
                style={{ fontSize: '12px', color: '#2563eb', fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              >
                Oznacz wszystkie
              </button>
            )}
          </div>

          <div style={{ flex: 1, overflowY: 'auto', color: '#0f172a' }}>
            {notifications.length === 0 ? (
              <div style={{ padding: '32px 16px', textAlign: 'center', fontSize: '14px', color: '#64748b' }}>
                <Bell className="w-12 h-12 mx-auto mb-2" style={{ color: '#94a3b8', opacity: 0.3 }} />
                <p>Brak powiadomień</p>
              </div>
            ) : (
              <div>
                {notifications.map((notification) => {
                  const isInactive = notification.active === false;

                  return (
                    <div
                      key={notification.id}
                      onClick={() => {
                        if (!notification.read && !isInactive) {
                          onMarkAsRead(notification.id);
                        }
                        if (!isInactive) {
                          onNotificationClick(notification);
                          setIsOpen(false);
                        }
                      }}
                      style={{
                        padding: '12px 16px',
                        cursor: isInactive ? 'default' : 'pointer',
                        opacity: isInactive ? 0.5 : 1,
                        backgroundColor: !notification.read && !isInactive ? '#eff6ff' : 'transparent',
                        borderBottom: '1px solid #e2e8f0',
                        transition: 'background-color 0.15s'
                      }}
                      onMouseEnter={(e) => !isInactive && (e.currentTarget.style.backgroundColor = '#f8fafc')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = !notification.read && !isInactive ? '#eff6ff' : 'transparent')}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', width: '100%' }}>
                        <div style={{ flexShrink: 0, marginTop: 2, color: isInactive ? '#9ca3af' : (notification.type === 'chat' ? '#3b82f6' : notification.type === 'warning' ? '#eab308' : notification.type === 'system' ? '#a855f7' : '#6b7280') }}>
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div style={{ flex: '1 1 auto', minWidth: '200px', width: 'calc(100% - 40px)' }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
                            <p 
                              style={{ 
                                fontSize: '14px', 
                                fontWeight: 600, 
                                color: isInactive ? '#9ca3af' : '#0f172a',
                                margin: 0,
                                lineHeight: 1.4,
                                flex: 1
                              }}
                            >
                              {notification.title || '(brak tytułu)'}
                            </p>
                            {!notification.read && !isInactive && (
                              <span style={{ flexShrink: 0, width: 8, height: 8, backgroundColor: '#3b82f6', borderRadius: '50%', marginTop: 6 }}></span>
                            )}
                          </div>
                          <p 
                            style={{ 
                              fontSize: '12px', 
                              margin: '4px 0 0 0',
                              color: isInactive ? '#9ca3af' : '#475569',
                              lineHeight: 1.4,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {notification.message || '(brak treści)'}
                          </p>
                          <p 
                            style={{ 
                              fontSize: '12px', 
                              margin: '4px 0 0 0',
                              color: isInactive ? '#9ca3af' : '#64748b'
                            }}
                          >
                            {formatTime(notification.createdAt || (notification as any).created_at)}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

